import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import {
  ClosedPeriodItemInitialState,
  ClosedPeriodItemCreate,
  ClosedPeriodItemDelete,
  ClosedPeriodItemLoad,
  ClosedPeriodItemUpdate,
} from './closed-period-item.actions';
import { catchError, map, take } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NotificationService } from '../../../../../../common/services/notification/notification.service';
import { ClosedPeriodItemService } from './closed-period-item.service';
import { ClosedPeriodListSearchCriteria, ClosedPeriodModel } from '../models';
import { ClosedPeriodListLoad } from '../list/closed-period-list.actions';
import { ListSearchCriteriaFacade, ListSearchCriteriaType } from '../../../../list-search-criteria';
import { CustomHttpErrorResponse, Error } from '../../../../../../common/constants/error';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface ClosedPeriodItemStateModel extends ClosedPeriodModel {
  loaded: boolean;
}

const initialState: ClosedPeriodItemStateModel = {
  ...new ClosedPeriodModel(),
  loaded: false,
};

@State<ClosedPeriodItemStateModel>({
  name: 'closedPeriodItem',
  defaults: initialState,
})
@Injectable()
export class ClosedPeriodItemState {
  constructor(
    private closedPeriodItemService: ClosedPeriodItemService,
    private notificationService: NotificationService,
    private listSearchCriteriaFacade: ListSearchCriteriaFacade,
  ) {}

  @Selector()
  static getState(state: ClosedPeriodItemStateModel): ClosedPeriodItemStateModel {
    return state;
  }

  @Selector()
  static getLoaded(state: ClosedPeriodItemStateModel): boolean {
    return state.loaded;
  }

  @Action(ClosedPeriodItemLoad)
  getPeriod(
    ctx: StateContext<ClosedPeriodItemStateModel>,
    { payload }: ClosedPeriodItemLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.closedPeriodItemService.getPeriodItem(payload).pipe(
      take(1),
      map((data: ClosedPeriodModel) => {
        ctx.patchState({ ...data, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ClosedPeriodItemCreate)
  createPeriod(
    ctx: StateContext<ClosedPeriodItemStateModel>,
    { payload }: ClosedPeriodItemCreate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.closedPeriodItemService.createPeriodItem(payload).pipe(
      take(1),
      map((period: ClosedPeriodModel) => {
        payload.dialog.close();
        this.notificationService.success('api.closedPeriod.success.create');
        ctx.patchState({ ...period, loaded: true });
        this.updateList(ctx, payload.compartmentId, payload.dockId);
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ClosedPeriodItemUpdate)
  updatePeriod(
    ctx: StateContext<ClosedPeriodItemStateModel>,
    { payload }: ClosedPeriodItemUpdate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.closedPeriodItemService.updatePeriodItem(payload).pipe(
      take(1),
      map((data: ClosedPeriodModel) => {
        payload.dialog.close();
        this.notificationService.success('api.closedPeriod.success.update');
        ctx.patchState({ ...data, loaded: true });
        this.updateList(ctx, payload.compartmentId, payload.dockId);
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ClosedPeriodItemDelete)
  deleteClosedPeriod(
    ctx: StateContext<ClosedPeriodItemStateModel>,
    { payload }: ClosedPeriodItemDelete,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | Observable<void>> {
    return this.closedPeriodItemService.deletePeriodItem(payload).pipe(
      take(1),
      map(() => {
        payload.dialog.close();
        this.notificationService.success('api.closedPeriod.success.delete');
        this.updateList(ctx, payload.compartmentId, payload.dockId);
        return ctx.dispatch(new ClosedPeriodItemInitialState());
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ClosedPeriodItemInitialState)
  setInitialState(ctx: StateContext<ClosedPeriodItemStateModel>): void {
    ctx.setState(initialState);
  }

  private updateList(ctx: StateContext<ClosedPeriodItemStateModel>, compartmentId: number, dockId: number): void {
    this.listSearchCriteriaFacade
      .getSearchCriteriaByListType(ListSearchCriteriaType.ClosedPeriods)
      .subscribe((searchCriteria: ClosedPeriodListSearchCriteria) =>
        ctx.dispatch(new ClosedPeriodListLoad({ compartmentId, dockId, searchCriteria })),
      );
  }
}
